import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, Pagination, Spinner } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';

import Tabela from '~/Components/TabelaDinamica/Tabela';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import {
  getPrecos,
  excluirPreco,
  importarPrecos,
  exportarPrecos,
} from '~/Services/Service';

import ModalPreco from './ModalPreco';

const ITENS_POR_PAGINA = 20;

const TabPrecos = ({ familia }) => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [precoEdit, setPrecoEdit] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [precoExcluir, setPrecoExcluir] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [importando, setImportando] = useState(false);
  const fileInputRef = useRef(null);

  const totalPaginas = Math.max(1, Math.ceil(dados.length / ITENS_POR_PAGINA));

  const dadosPaginados = useMemo(
    () =>
      dados.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA),
    [dados, pagina]
  );

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  useEffect(() => {
    setPagina(1);
  }, [familia]);

  const mudarPagina = (n) => {
    if (n < 1 || n > totalPaginas) return;
    setPagina(n);
  };

  const carregarDados = useCallback(async () => {
    if (!familia) return;
    setLoading(true);
    try {
      const res = await getPrecos(familia);
      setDados(res || []);
    } catch {
      toast.error('Erro ao carregar preços');
    } finally {
      setLoading(false);
    }
  }, [familia]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const handleNovo = () => {
    setPrecoEdit(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setPrecoEdit(item);
    setShowModal(true);
  };

  const handleExcluir = (item) => {
    setPrecoExcluir(item);
    setShowConfirm(true);
  };

  const confirmarExclusao = async (confirmado) => {
    setShowConfirm(false);
    if (!confirmado) return;
    try {
      await excluirPreco(
        precoExcluir.codigo_familia,
        precoExcluir.codigo_qualidade
      );
      toast.success('Preço excluído com sucesso');
      carregarDados();
    } catch {
      toast.error('Erro ao excluir preço');
    }
  };

  const handleSalvar = () => {
    setShowModal(false);
    carregarDados();
  };

  const handleExportar = async () => {
    try {
      const res = await exportarPrecos(familia);
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'precos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao exportar preços');
    }
  };

  const handleImportar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('familia', familia);
    setImportando(true);
    try {
      const res = await importarPrecos(formData);
      toast.success(`${(res && res.importados) || 0} preços importados`);
      carregarDados();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao importar preços';
      toast.error(msg);
    } finally {
      setImportando(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const tipoTabelaLabel = (v) => {
    const map = { C: 'Comercial', M: 'Misto', R: 'Residencial', V: 'Vertical' };
    return map[v] || v;
  };

  const tipoEntradaLabel = (v) => {
    const map = { 1: 'Fixo', 2: '% Preço à Vista', 3: '% PV 1ª Fixa' };
    return map[v] || v;
  };

  const colunas = [
    { title: 'Qualidade', key: 'nome_qualidade' },
    {
      title: 'Tipo',
      key: 'tipo_tabela',
      width: '100px',
      render: (v) => tipoTabelaLabel(v),
    },
    {
      title: 'Valor M²',
      key: 'valor_m2',
      width: '100px',
      render: (v) =>
        v != null
          ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
          : '',
    },
    {
      title: 'Fixo',
      key: 'fixo',
      width: '80px',
      render: (v) =>
        v != null
          ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
          : '',
    },
    {
      title: 'Vl. Entrada',
      key: 'valor_entrada',
      width: '100px',
      render: (v) =>
        v != null
          ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
          : '',
    },
    {
      title: 'Tipo Entrada',
      key: 'tipo_entrada',
      width: '110px',
      render: (v) => tipoEntradaLabel(v),
    },
    {
      title: 'Sinal',
      key: 'entrada_sinal',
      width: '80px',
      render: (v) =>
        v != null
          ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
          : '',
    },
    {
      title: 'Tx Entrada',
      key: 'tx_entrada',
      width: '90px',
      render: (v) => (v != null ? `${v}%` : ''),
    },
    { title: 'Qtde Parc.', key: 'qtde_parcela', width: '90px' },
    {
      title: 'Desconto',
      key: 'desconto',
      width: '80px',
      render: (v) => (v != null ? `${v}%` : ''),
    },
  ];

  const acoes = [
    { label: '', title: 'Editar', icon: <FaEdit />, onClick: handleEditar },
    {
      label: '', title: 'Excluir',
      icon: <FaTrash />,
      onClick: handleExcluir,
      type: 'danger',
    },
  ];

  if (loading) return <Loading />;

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleImportar}
        />
        <Button
          variant="outline-success"
          size="sm"
          className="mr-2"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={importando}
        >
          {importando ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="mr-1"
              />
              Importando...
            </>
          ) : (
            <>
              <FaFileExcel className="mr-1" /> Importar
            </>
          )}
        </Button>
        <Button
          variant="outline-success"
          size="sm"
          className="mr-2"
          onClick={handleExportar}
        >
          <FaFileExcel className="mr-1" /> Exportar
        </Button>
        <Button variant="primary" size="sm" onClick={handleNovo}>
          <FaPlus className="mr-1" /> Novo Preço
        </Button>
      </div>

      <Tabela colunas={colunas} dados={dadosPaginados} acoes={acoes} action />

      {dados.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            Mostrando {(pagina - 1) * ITENS_POR_PAGINA + 1} até{' '}
            {Math.min(pagina * ITENS_POR_PAGINA, dados.length)} de{' '}
            {dados.length} registros
          </div>
          {totalPaginas > 1 && (
            <Pagination className="mb-0">
              <Pagination.First
                onClick={() => mudarPagina(1)}
                disabled={pagina === 1}
              />
              <Pagination.Prev
                onClick={() => mudarPagina(pagina - 1)}
                disabled={pagina === 1}
              />
              {(() => {
                const maxBotoes = 5;
                let inicio = Math.max(1, pagina - Math.floor(maxBotoes / 2));
                let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
                if (fim - inicio < maxBotoes - 1) {
                  inicio = Math.max(1, fim - maxBotoes + 1);
                }
                const itens = [];
                if (inicio > 1) {
                  itens.push(
                    <Pagination.Ellipsis key="ell-start" disabled />
                  );
                }
                for (let n = inicio; n <= fim; n += 1) {
                  itens.push(
                    <Pagination.Item
                      key={n}
                      active={n === pagina}
                      onClick={() => mudarPagina(n)}
                    >
                      {n}
                    </Pagination.Item>
                  );
                }
                if (fim < totalPaginas) {
                  itens.push(<Pagination.Ellipsis key="ell-end" disabled />);
                }
                return itens;
              })()}
              <Pagination.Next
                onClick={() => mudarPagina(pagina + 1)}
                disabled={pagina === totalPaginas}
              />
              <Pagination.Last
                onClick={() => mudarPagina(totalPaginas)}
                disabled={pagina === totalPaginas}
              />
            </Pagination>
          )}
        </div>
      )}

      <ModalPreco
        show={showModal}
        onHide={() => setShowModal(false)}
        onSalvar={handleSalvar}
        preco={precoEdit}
        familia={familia}
      />

      <ModalConfirmacao
        show={showConfirm}
        onClose={confirmarExclusao}
        title="Excluir Preço"
        message="Deseja realmente excluir este preço?"
      />
    </>
  );
};

export default TabPrecos;
